import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useToast } from '../lib/toast';
import { locName } from '../lib/constants';
import BookingsCalendar from './BookingsCalendar';

const STATUS_COL = {
  Pending: { bg: '#FEF3C7', tx: '#92400E' },
  Confirmed: { bg: '#DBEAFE', tx: '#1E40AF' },
  Completed: { bg: '#DCFCE7', tx: '#166534' },
  Cancelled: { bg: '#FEE2E2', tx: '#991B1B' },
};

export default function BookingsView() {
  const { show } = useToast();
  const [bookings, setBookings] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState('All');
  const [view, setView] = useState('list'); // 'list' | 'calendar'

  async function load() {
    setLoading(true);
    try {
      const [bk, vh] = await Promise.all([api.get('/bookings'), api.get('/vehicles')]);
      setBookings(bk.data);
      setFleet(vh.data);
    } catch (e) {
      show('Failed to load bookings', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function updateStatus(b, status) {
    try {
      const { data } = await api.put(`/bookings/${b.id}/status`, { status });
      setBookings((p) => p.map((x) => (x.id === b.id ? data : x)));
      show(`Booking ${status.toLowerCase()}`, 'success');
    } catch (e) {
      show('Update failed', 'error');
    }
  }

  async function deleteBooking(id) {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await api.delete(`/bookings/${id}`);
      setBookings((p) => p.filter((x) => x.id !== id));
      show('Booking deleted', 'success');
    } catch (e) {
      show('Delete failed', 'error');
    }
  }

  async function resendEmail(b) {
    try {
      const { data } = await api.post(`/bookings/${b.id}/resend-confirmation`);
      setBookings((p) =>
        p.map((x) => (x.id === b.id ? { ...x, email_sent: true, email_provider_id: data.email_id } : x))
      );
      show('Confirmation email sent', 'success');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Email send failed';
      show(typeof detail === 'string' ? detail : 'Email send failed', 'error');
    }
  }

  const filtered = bookings.filter((b) => {
    const q = search.toLowerCase();
    return (
      (!q ||
        (b.customer_name + ' ' + b.customer_email + ' ' + b.vehicle_label + ' ' + b.id).toLowerCase().includes(q)) &&
      (statusF === 'All' || b.status === statusF)
    );
  });

  const totalRevenue = bookings
    .filter((b) => b.status === 'Confirmed' || b.status === 'Completed')
    .reduce((s, b) => s + (b.total || 0), 0);
  const pendingCount = bookings.filter((b) => b.status === 'Pending').length;

  return (
    <div data-testid="bookings-view">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
        <MiniStat label="Total Bookings" value={bookings.length} icon="📅" color="#0D1B3E" testId="bk-stat-total" />
        <MiniStat label="Pending Approval" value={pendingCount} icon="⏳" color="#F59E0B" testId="bk-stat-pending" />
        <MiniStat label="Confirmed Revenue" value={`$${totalRevenue.toFixed(2)}`} icon="💰" color="#10B981" testId="bk-stat-revenue" />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 10, padding: 2 }}>
          {[
            ['list', '☰ List'],
            ['calendar', '📅 Calendar'],
          ].map(([k, l]) => (
            <button
              key={k}
              data-testid={`bk-view-${k}`}
              onClick={() => setView(k)}
              style={{
                padding: '7px 14px',
                borderRadius: 8,
                border: 'none',
                background: view === k ? '#0D1B3E' : 'transparent',
                color: view === k ? '#fff' : '#64748B',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {l}
            </button>
          ))}
        </div>
        {view === 'list' && (
          <>
            <input
              data-testid="bk-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by customer, email, vehicle, ID..."
              style={{ flex: 1, minWidth: 240, padding: '10px 14px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13 }}
            />
            <select
              data-testid="bk-status-filter"
              value={statusF}
              onChange={(e) => setStatusF(e.target.value)}
              style={{ padding: '10px 12px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13, background: '#fff' }}
            >
              <option>All</option>
              {['Pending', 'Confirmed', 'Completed', 'Cancelled'].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94A3B8' }}>Loading bookings…</div>
      ) : view === 'calendar' ? (
        <BookingsCalendar
          bookings={bookings}
          fleet={fleet}
          onPick={(b) => {
            setView('list');
            setSearch(b.id);
          }}
        />
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['Booking', 'Customer', 'Vehicle', 'Dates', 'Pickup → Drop-off', 'Total', 'Status', 'Actions'].map((h) => (
                  <th
                    key={h}
                    style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const sc = STATUS_COL[b.status] || STATUS_COL.Pending;
                return (
                  <tr key={b.id} data-testid={`row-booking-${b.id}`} style={{ borderTop: '1px solid #E2E8F0' }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 800, fontSize: 12, color: '#0D1B3E', letterSpacing: '.02em' }}>#{b.id.slice(-8).toUpperCase()}</div>
                      <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{new Date(b.created_at).toLocaleDateString()}</div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#0F172A' }}>{b.customer_name}</div>
                      <div style={{ fontSize: 11, color: '#64748B' }}>{b.customer_email}</div>
                      {b.customer_phone && <div style={{ fontSize: 11, color: '#94A3B8' }}>{b.customer_phone}</div>}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 13, color: '#0F172A', fontWeight: 600 }}>{b.vehicle_label}</td>
                    <td style={{ padding: '14px 16px', fontSize: 12, color: '#0F172A' }}>
                      <div>{b.pickup_date} →</div>
                      <div>{b.dropoff_date}</div>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>{b.days} day{b.days > 1 ? 's' : ''}</div>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 12, color: '#475569' }}>
                      <div>{locName(b.pickup_location)}</div>
                      {b.pickup_location !== b.dropoff_location && (
                        <div style={{ color: '#94A3B8' }}>↓ {locName(b.dropoff_location)}</div>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 900, fontSize: 16, color: '#0D1B3E' }}>${b.total.toFixed(2)}</div>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>+${b.deposit.toFixed(0)} dep.</div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ background: sc.bg, color: sc.tx, borderRadius: 20, padding: '3px 11px', fontSize: 11, fontWeight: 700 }}>{b.status}</span>
                      {b.payment_status === 'paid' && (
                        <div
                          title={b.email_skipped_reason || (b.email_sent_at ? `Sent ${new Date(b.email_sent_at).toLocaleString()}` : '')}
                          style={{ marginTop: 6, fontSize: 10, color: b.email_sent ? '#166534' : '#94A3B8', fontWeight: 700 }}
                        >
                          {b.email_sent ? '✉ email sent' : `○ ${b.email_skipped_reason ? 'email skipped' : 'email pending'}`}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {b.status === 'Pending' && (
                          <button
                            data-testid={`confirm-booking-${b.id}`}
                            onClick={() => updateStatus(b, 'Confirmed')}
                            style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #BFDBFE', background: '#EFF6FF', fontSize: 11, fontWeight: 700, color: '#1E40AF', cursor: 'pointer' }}
                          >
                            ✓ Confirm
                          </button>
                        )}
                        {(b.status === 'Pending' || b.status === 'Confirmed') && (
                          <button
                            data-testid={`complete-booking-${b.id}`}
                            onClick={() => updateStatus(b, 'Completed')}
                            style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #BBF7D0', background: '#F0FDF4', fontSize: 11, fontWeight: 700, color: '#166534', cursor: 'pointer' }}
                          >
                            ✓ Complete
                          </button>
                        )}
                        {b.payment_status === 'paid' && (
                          <button
                            data-testid={`resend-email-${b.id}`}
                            onClick={() => resendEmail(b)}
                            title={b.email_sent ? 'Re-send confirmation email' : 'Send confirmation email'}
                            style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #E0E7FF', background: '#EEF2FF', fontSize: 11, fontWeight: 700, color: '#3730A3', cursor: 'pointer' }}
                          >
                            ✉ {b.email_sent ? 'Resend' : 'Send'}
                          </button>
                        )}
                        {b.status !== 'Cancelled' && b.status !== 'Completed' && (
                          <button
                            data-testid={`cancel-booking-${b.id}`}
                            onClick={() => updateStatus(b, 'Cancelled')}
                            style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #FECACA', background: '#FFF5F5', fontSize: 11, fontWeight: 700, color: '#991B1B', cursor: 'pointer' }}
                          >
                            ✕ Cancel
                          </button>
                        )}
                        <button
                          data-testid={`delete-booking-${b.id}`}
                          onClick={() => deleteBooking(b.id)}
                          style={{ padding: '6px 8px', borderRadius: 7, border: '1.5px solid #E2E8F0', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', color: '#64748B' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: '50px', textAlign: 'center', color: '#94A3B8', fontSize: 14 }}>
                    {bookings.length === 0 ? 'No bookings yet — waiting for customers!' : 'No bookings match your filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, icon, color, testId }) {
  return (
    <div data-testid={testId} style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: '16px 18px' }}>
      <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: '#64748B', fontWeight: 700, marginTop: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
    </div>
  );
}
